import React, { useRef, useState, useEffect } from 'react';
import { ComplaintWizardState } from '../NewComplaintModal';
import styles from './ImageUploadStep.module.css';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import ImageOverlay from '../Shared/ImageViewer/ImageOverlay';

interface ImageUploadStepProps {
    data: ComplaintWizardState;
    updateData: (updates: Partial<ComplaintWizardState>) => void;
    goToStep?: (step: number) => void;
    highlightLabels?: boolean;
    highlightImages?: boolean;
}

interface ImageMeta {
    file: File;
    preview: string;
    status: 'pending' | 'optimized';
    originalSize: number;
    compressedSize?: number;
}

const MAX_IMAGES = 15;

const ImageUploadStep: React.FC<ImageUploadStepProps> = ({ data, updateData, goToStep, highlightLabels, highlightImages }) => {
    const [labelSectionRef, labelInputRef] = [useRef<HTMLDivElement>(null), useRef<HTMLInputElement>(null)];
    const [imageSectionRef, imageInputRef] = [useRef<HTMLDivElement>(null), useRef<HTMLInputElement>(null)];
    const [images, setImages] = useState<ImageMeta[]>([]);
    const [labelImages, setLabelImages] = useState<ImageMeta[]>([]);
    const [previewIndex, setPreviewIndex] = useState<{ index: number, type: 'foto' | 'etiqueta' } | null>(null);

    // Initialize local state from wizard data (if coming back from next step)
    useEffect(() => {
        if (images.length === 0 && data.images.length > 0) {
            const initialImages = data.images.map(file => ({
                file,
                preview: URL.createObjectURL(file),
                status: 'optimized' as const,
                originalSize: file.size,
                compressedSize: file.size
            }));
            setImages(initialImages);
        }
        if (labelImages.length === 0 && data.labelImages.length > 0) {
            const initialLabels = data.labelImages.map(file => ({
                file,
                preview: URL.createObjectURL(file),
                status: 'optimized' as const,
                originalSize: file.size,
                compressedSize: file.size
            }));
            setLabelImages(initialLabels);
        }
    }, []);

    // Sync back to Wizard Data
    useEffect(() => {
        const files = images.map(img => img.file);
        const labelFiles = labelImages.map(img => img.file);

        // Only update if lengths changed or files changed (simple check)
        if (files.length !== data.images.length || labelFiles.length !== data.labelImages.length) {
            updateData({ images: files, labelImages: labelFiles });
        }
    }, [images, labelImages, updateData, data.images.length, data.labelImages.length]);

    // Handle validation highlight scroll
    useEffect(() => {
        if (highlightImages && imageSectionRef.current) {
            imageSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else if (highlightLabels && labelSectionRef.current) {
            labelSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [highlightLabels, highlightImages]);

    const handleFiles = (newFiles: File[], type: 'foto' | 'etiqueta') => {
        const currentImages = type === 'foto' ? images : labelImages;

        // Check image limit
        if (currentImages.length + newFiles.length > MAX_IMAGES) {
            alert(`Você pode adicionar no máximo ${MAX_IMAGES} fotos. Atualmente você tem ${currentImages.length} foto(s).`);
            return;
        }

        const newImages: ImageMeta[] = newFiles.map(file => ({
            file,
            preview: URL.createObjectURL(file),
            status: 'pending',
            originalSize: file.size
        }));

        const setTargetImages = type === 'foto' ? setImages : setLabelImages;

        setTargetImages(prev => [...prev, ...newImages]);

        // Simulate optimization with size reduction
        newImages.forEach(img => {
            setTimeout(() => {
                const compressedSize = Math.floor(img.originalSize * (0.3 + Math.random() * 0.2)); // 30-50% of original
                setTargetImages(prev => prev.map(p =>
                    p.preview === img.preview ? { ...p, status: 'optimized', compressedSize } : p
                ));
            }, 1500 + Math.random() * 1000);
        });
    };

    const onChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'foto' | 'etiqueta') => {
        if (e.target.files) {
            handleFiles(Array.from(e.target.files), type);
        }
    };

    const handleRemove = (index: number, type: 'foto' | 'etiqueta') => {
        const setTargetImages = type === 'foto' ? setImages : setLabelImages;
        setTargetImages(prev => {
            const newImages = [...prev];
            URL.revokeObjectURL(newImages[index].preview);
            newImages.splice(index, 1);
            return newImages;
        });
    };

    const onDrop = (e: React.DragEvent, type: 'foto' | 'etiqueta') => {
        e.preventDefault();
        if (e.dataTransfer.files) {
            handleFiles(Array.from(e.dataTransfer.files), type);
        }
    };

    const openPreview = (index: number, type: 'foto' | 'etiqueta') => {
        setPreviewIndex({ index, type });
    };

    const closePreview = () => {
        setPreviewIndex(null);
    };

    const renderDropzone = (type: 'foto' | 'etiqueta', title: string, currentImages: ImageMeta[], ref: React.RefObject<HTMLInputElement | null>, sectionRef?: React.RefObject<HTMLDivElement | null>) => {
        const isHighlighted = (type === 'etiqueta' && highlightLabels) || (type === 'foto' && highlightImages);

        return (
            <div className={`${styles.highlightSection} ${isHighlighted ? styles.errorHighlight : ''}`} ref={sectionRef}>
                <div className={styles.sectionContainer}>
                    <h3 className={styles.sectionTitle}>{title}</h3>

                    {currentImages.length === 0 ? (
                        <div
                            className={styles.dropzone}
                            onClick={() => ref.current?.click()}
                            onDragOver={e => e.preventDefault()}
                            onDrop={(e) => onDrop(e, type)}
                        >
                            <CloudUploadIcon className={styles.dropzoneIcon} />
                            <div className={styles.dropzoneText}>Clique ou arraste fotos aqui</div>
                            <div className={styles.dropzoneSubtext}>
                                Suporta JPG, PNG, WEBP (Convertidos auto p/ WebP)
                            </div>
                        </div>
                    ) : (
                        <div className={styles.previewGrid}>
                            <div
                                className={styles.addMoreCard}
                                onClick={() => ref.current?.click()}
                                onDragOver={e => e.preventDefault()}
                                onDrop={(e) => onDrop(e, type)}
                            >
                                <CloudUploadIcon className={styles.addMoreIcon} />
                                <span className={styles.addMoreText}>Adicionar</span>
                                <div className={styles.imageCounter} style={{ marginTop: 0, padding: '2px 8px' }}>
                                    {currentImages.length} / {MAX_IMAGES}
                                </div>
                            </div>

                            {currentImages.map((img, index) => (
                                <div
                                    key={index}
                                    className={styles.previewCard}
                                    onClick={() => openPreview(index, type)}
                                    style={{ cursor: 'pointer' }}
                                    title="Clique para ampliar"
                                >
                                    <img src={img.preview} alt="Preview" className={styles.previewImage} />
                                    <button className={styles.removeButton} onClick={(e) => { e.stopPropagation(); handleRemove(index, type); }}>
                                        <CloseIcon fontSize="small" />
                                    </button>
                                    <div className={`${styles.badge} ${img.status === 'pending' ? styles.badgeProcessing : styles.badgeOptimized}`}>
                                        {img.status === 'pending' ? 'Otimizando...' : 'WebP'}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <input
                        type="file"
                        multiple
                        accept="image/*"
                        className={styles.hiddenInput}
                        ref={ref}
                        onChange={(e) => onChange(e, type)}
                    />
                </div>
            </div>
        );
    };

    const activePreviewImages = previewIndex?.type === 'foto' ? images : labelImages;

    return (
        <div className={styles.container}>
            <div className={styles.splitLayout}>
                {renderDropzone('foto', 'Fotos do Produto (Obrigatório)', images, imageInputRef, imageSectionRef)}
                {renderDropzone('etiqueta', 'Fotos da Etiqueta (Obrigatório)', labelImages, labelInputRef, labelSectionRef)}
            </div>

            {previewIndex !== null && activePreviewImages.length > 0 && (
                <ImageOverlay
                    images={activePreviewImages.map(img => img.file)}
                    initialIndex={previewIndex.index}
                    onClose={closePreview}
                />
            )}
        </div>
    );
};

export default ImageUploadStep;
